const toNumberOrZero = (value) => {
  const n = parseFloat(value);
  return isNaN(n) ? 0 : n;
};

const entityReference = (entity) => (entity.id === 'MATRIZ'
  ? { entityType: 'MATRIZ', entityBranchId: null }
  : { entityType: 'SUCURSAL', entityBranchId: entity.id ?? null });

export const buildQuotationLines = (selectedEntities, additionalServices, editablePrices) => {
  const allServices = [];
  let consolidatedTotal = 0;

  (selectedEntities || []).forEach((entity) => {
    const entityLabel = entity.name || entity.branchName || entity.businessName || 'Ente';
    const entityRef = entityReference(entity);

    (entity.selectedServices || []).forEach((service) => {
      const basePrice = editablePrices[service.id] !== undefined
        ? editablePrices[service.id]
        : (typeof service.price === 'number' ? service.price : 0);
      const validPrice = isNaN(basePrice) ? 0 : Number(basePrice);

      allServices.push({
        serviceDescription: service.serviceName || service.name || 'Servicio Principal',
        unitPrice: validPrice,
        notes: `Servicio principal - ${service.serviceName || service.name} (${entityLabel})`,
        lineKind: 'MAIN_SERVICE',
        serviceId: service.id ?? null,
        ...entityRef
      });
      consolidatedTotal += validPrice;
    });

    (entity.additionalServices || []).forEach((service) => {
      const price = parseFloat(service.unitPrice);
      const validPrice = isNaN(price) ? 0 : price;
      allServices.push({
        serviceDescription: service.serviceDescription,
        unitPrice: validPrice,
        notes: service.notes ? `${service.notes} (${entityLabel})` : `(${entityLabel})`,
        ...(service._isSubstanceRecargo
          ? { lineKind: 'SURCHARGE', surchargePct: toNumberOrZero(service._recargoPct), surchargeFixed: toNumberOrZero(service._recargoFixed) }
          : { lineKind: 'ADDITIONAL' }),
        ...entityRef
      });
      consolidatedTotal += validPrice;
    });
  });

  (additionalServices || []).forEach((service) => {
    const price = parseFloat(service.unitPrice);
    const validPrice = isNaN(price) ? 0 : price;
    allServices.push({
      serviceDescription: service.serviceDescription,
      unitPrice: validPrice,
      notes: service.notes || '',
      lineKind: 'GLOBAL_ADDITIONAL'
    });
    consolidatedTotal += validPrice;
  });

  return { totalAmount: consolidatedTotal, additionalServices: allServices };
};

export const buildPricingInput = (selectedEntities, additionalServices, editablePrices) => {
  const priceOverrides = {};
  Object.keys(editablePrices || {}).forEach((id) => {
    const value = editablePrices[id];
    priceOverrides[id] = isNaN(value) ? 0 : Number(value);
  });

  return {
    entities: (selectedEntities || []).map((entity) => ({
      label: entity.name || entity.branchName || entity.businessName || 'Ente',
      services: (entity.selectedServices || []).map((service) => ({
        serviceId: service.id,
        name: service.serviceName || service.name,
        catalogPrice: typeof service.price === 'number' ? service.price : 0
      })),
      extras: (entity.additionalServices || []).map((line) => ({
        description: line.serviceDescription,
        unitPrice: toNumberOrZero(line.unitPrice),
        notes: line.notes || '',
        ...(line._isSubstanceRecargo
          ? { surchargePct: toNumberOrZero(line._recargoPct), surchargeFixed: toNumberOrZero(line._recargoFixed) }
          : {})
      }))
    })),
    globalExtras: (additionalServices || []).map((line) => ({
      description: line.serviceDescription,
      unitPrice: toNumberOrZero(line.unitPrice),
      notes: line.notes || ''
    })),
    priceOverrides
  };
};

const SURCHARGE_PREFIX = 'Recargo sustancia peligrosa:';

const stripEntityLabel = (notes, label) => {
  if (!notes) return '';
  if (notes === `(${label})`) return '';
  const suffix = ` (${label})`;
  return notes.endsWith(suffix) ? notes.slice(0, -suffix.length).trim() : notes;
};

const isRestorable = (lines) => lines.length > 0 && lines.every((l) =>
  l.lineKind
  && (l.lineKind !== 'MAIN_SERVICE' || l.serviceId != null)
  && (l.lineKind === 'GLOBAL_ADDITIONAL' || l.entityType));

const belongsTo = (line, entity) => (line.entityType === 'MATRIZ'
  ? entity.id === 'MATRIZ'
  : entity.id !== 'MATRIZ' && String(entity.id) === String(line.entityBranchId));

export const restoreQuotationLines = (lines, entities) => {
  const savedLines = lines || [];
  if (!isRestorable(savedLines)) return null;

  const editablePrices = {};
  savedLines.filter((l) => l.lineKind === 'MAIN_SERVICE').forEach((l) => {
    editablePrices[l.serviceId] = toNumberOrZero(l.unitPrice);
  });

  const assigned = new Set();
  const restoredEntities = (entities || []).map((entity) => {
    const label = entity.name || entity.branchName || entity.businessName || 'Ente';
    const usedSubstances = new Set();
    const extras = savedLines
      .filter((l) => l.lineKind !== 'MAIN_SERVICE' && l.lineKind !== 'GLOBAL_ADDITIONAL' && belongsTo(l, entity))
      .map((l) => {
        assigned.add(l);
        const restored = {
          id: l.id || Date.now() + Math.random(),
          serviceDescription: l.serviceDescription || '',
          unitPrice: l.unitPrice || 0,
          notes: stripEntityLabel(l.notes, label)
        };
        if (l.lineKind !== 'SURCHARGE') return restored;
        const substanceName = (l.serviceDescription || '').replace(SURCHARGE_PREFIX, '').trim().toLowerCase();
        const substance = (entity.hazardousSubstancesList || []).find((s) =>
          !usedSubstances.has(s.id) && (s.substanceName || '').trim().toLowerCase() === substanceName);
        if (!substance) return restored;
        usedSubstances.add(substance.id);
        return {
          ...restored,
          _isSubstanceRecargo: true,
          _recargoPct: toNumberOrZero(l.surchargePct),
          _recargoFixed: toNumberOrZero(l.surchargeFixed),
          _substanceId: substance.id
        };
      });
    return { ...entity, additionalServices: extras };
  });

  const globalAdditional = savedLines
    .filter((l) => l.lineKind !== 'MAIN_SERVICE' && !assigned.has(l))
    .map((l) => ({
      id: l.id || Date.now() + Math.random(),
      serviceDescription: l.serviceDescription || '',
      unitPrice: l.unitPrice || 0,
      notes: l.notes || ''
    }));

  return { entities: restoredEntities, globalAdditional, editablePrices };
};
